import { fetchPermissionConfigQueryOptions } from '@/api/permissionsQueryOptions'
import { ApiErrorComponent } from '@/components/ApiErrorComponent'
import { useColumnState } from '@/components/hooks/useColumnState'
import useGridPageSize from '@/components/hooks/useGridPageSize'
import LoadProgress from '@/components/LoadProgress'
import { OverviewGridToolbar } from '@/components/mui-x/datagrid/OverviewGridToolbar'
import Box from '@mui/material/Box'
import { DataGridPremium, GridColDef, useGridApiRef } from '@mui/x-data-grid-premium'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { t } from 'i18next'
import React from 'react'

export const Route = createFileRoute('/_auth/permissionConfig')({
    component: PermissionConfigComponent,
    errorComponent: ({ error }) => {
        if (error) {
            return <ApiErrorComponent error={error as Error} />
        } else {
            return <ApiErrorComponent />
        }
    },
})

function PermissionConfigComponent() {
    const permissionConfigQuery = useSuspenseQuery(
        fetchPermissionConfigQueryOptions(),
    )
    const permissionConfig = permissionConfigQuery.data

    const columns: GridColDef[] = []

    columns.push(
        {
            field: 'role.name',
            headerName: 'Rolle',
            valueGetter: (value, row) => {
                return value || row.role?.name || ''
            },
            type: 'singleSelect',
            valueOptions: Array.from(new Set(permissionConfigQuery.data?.map((config) => config.role.name) || [])),
        },
        {
            field: 'code',
            headerName: 'Code',
            valueGetter: (_value, row) => {
                return row.base + (row.detail ? '.' + row.detail : '')
            }
        },
        {
            field: 'base',
            headerName: 'Datenelement',
            valueGetter: (_value, row) => {
                if (row.detail && row.detail[0] === row.detail[0].toUpperCase()) {
                    return t(['entity.' + row.detail[0].toLowerCase() + row.detail.slice(1), 'permissionConfig.' + row.base + '.' + row.detail]);
                } else if (row.detail) {
                    return t(['property.' + row.base[0].toLowerCase() + row.base.slice(1) + '.' + row.detail, 'permissionConfig.' + row.base + '.' + row.detail]);
                }
                return t(['entity.' + row.base[0].toLowerCase() + row.base.slice(1), 'permissionConfig.' + row.base + '.' + row.detail]);
            }
        },
        {
            field: 'inheritance',
            headerName: 'Datensätze aus',
            valueGetter: (value) => {
                return value && t('enum.permissionConfig.inheritance.' + value);
            }
        },
        {
            field: 'inheritanceDetail',
            headerName: 'Einschränkung',
        },
        {
            field: 'read',
            headerName: 'Lesen',
            type: 'boolean',
        },
        {
            field: 'create',
            headerName: 'Erstellen',
            type: 'boolean',
        },
        {
            field: 'write',
            headerName: 'Ändern',
            type: 'boolean',
        },
        {
            field: 'delete',
            headerName: 'Löschen',
            type: 'boolean',
        },
        {
            field: 'assign',
            headerName: 'Zuordnen',
            type: 'boolean',
        },
    )

    const gridPageSize = useGridPageSize()
    const pageSizeOptions = gridPageSize.getPageSizeOptions('permissionConfig', 35)
    const [paginationModel, setPaginationModel] = React.useState(gridPageSize.getPaginationModel('permissionConfig', 35));

    const apiRef = useGridApiRef()
    const columnsState = useColumnState('permissionConfig', apiRef, columns)

    const refreshData = () => {
        permissionConfigQuery.refetch()
    }

    if (
        permissionConfigQuery.isLoading ||
        permissionConfigQuery.isFetching ||
        !permissionConfig
    ) {
        return <LoadProgress />
    }

    return (
        <Box sx={{
            height: { xs: `calc(100% - 50px)` }
        }}>
            <DataGridPremium
                columns={columnsState.columns}
                onColumnWidthChange={columnsState.onColumnWidthChange}
                onColumnOrderChange={columnsState.onColumnOrderChange}
                rows={permissionConfig}
                getRowId={(row) => {
                    return row.uuid || ''
                }}
                pagination
                pageSizeOptions={pageSizeOptions}
                paginationModel={paginationModel}
                onPaginationModelChange={(model) => {
                    setPaginationModel(model)
                    gridPageSize.handlePaginationModelChange('permissionConfig', model)
                }}
                slots={{ toolbar: (props) => { return <OverviewGridToolbar {...props} scope="permissionConfig" refresh={refreshData} enablePivoting /> } }}
                slotProps={{
                    toolbar: {
                        csvOptions: {
                            disableToolbarButton: true
                        },
                        printOptions: {
                            disableToolbarButton: true
                        }
                    }
                }}
                density='compact'
                hideFooter={true}
                disableDensitySelector
                disableAggregation
                disableColumnPinning
                disableRowGrouping
                disableRowSelectionOnClick
                showToolbar />
        </Box>
    );
}
